import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import useSeo from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/site';

const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';
const catName = (id: string) => categories.find((c) => c.id === id)?.name ?? '专题';

function ReadingProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="fixed top-16 md:top-20 left-0 right-0 z-40 h-0.5 bg-transparent">
      <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
    </div>
  );
}

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const topic = topics.find((t) => t.id === article?.topicId);

  const siblings = article ? articles.filter((a) => a.topicId === article.topicId) : [];
  const idx = siblings.findIndex((a) => a.id === id);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
  const moreList = article ? articles.filter((a) => a.id !== article.id).slice(0, 4) : [];

  const jsonLd = article
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.excerpt,
          datePublished: article.publishTime,
          articleSection: catName(topic?.categoryId ?? ''),
          keywords: article.tags.join(','),
          url: `${SITE_URL}/article/${article.id}`,
          author: { '@type': 'Organization', name: '看料社' },
          publisher: { '@type': 'Organization', name: '看料社' },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: '资源库', item: `${SITE_URL}/library` },
            {
              '@type': 'ListItem',
              position: 3,
              name: topic?.title ?? '专题',
              item: `${SITE_URL}/topic/${topic?.id ?? ''}`,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: article.title,
              item: `${SITE_URL}/article/${article.id}`,
            },
          ],
        },
      ]
    : [];

  useSeo({
    title: article ? `${article.title} - 看料社专题整理` : '内容不存在｜看料社',
    description: article
      ? `${article.excerpt} 本文属于「${topic?.title ?? '看料社'}」专题，约 ${article.readMinutes} 分钟读完。`
      : '该内容可能已被调整，请返回资源库浏览其他在线看料专题。',
    keywords: article
      ? `${article.tags.join(',')},在线看料,看料专题整理`
      : '在线看料,看料资源库',
    type: 'article',
    jsonLd,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-20 max-w-content mx-auto px-4 md:px-6 py-24 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-5">
            <i className="ri-article-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这篇内容</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经被调整，去资源库看看其他整理吧。</p>
          <Link
            to="/library"
            className="inline-block mt-6 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
          >
            返回资源库
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <ReadingProgress />
      <main className="pt-16 md:pt-20">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '资源库', to: '/library' },
                { label: topic?.title ?? '专题', to: `/topic/${topic?.id}` },
                { label: article.title },
              ]}
            />
            <span className="inline-block mt-5 px-2.5 py-1 rounded-md text-xs bg-primary-50 text-primary-600">
              {catName(topic?.categoryId ?? '')}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.publishTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                约 {article.readMinutes} 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-folder-line" />
                {topic?.title}
              </span>
            </div>
          </div>
        </section>

        <article className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-content mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2 max-w-3xl">
              <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden bg-background-200 mb-8">
                <img
                  src={topic?.cover}
                  alt={`${article.title} - 看料社专题配图`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <p className="text-base text-foreground-900 leading-relaxed font-medium border-l-2 border-primary-500 pl-4 mb-8">
                {article.excerpt}
              </p>

              <div className="flex flex-col gap-5">
                {article.content.map((para, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/library?q=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {prev ? (
                  <Link
                    to={`/article/${prev.id}`}
                    className="group p-4 rounded-lg border border-background-200 hover:border-primary-300 transition-colors"
                  >
                    <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                      <i className="ri-arrow-left-line" /> 上一篇
                    </span>
                    <span className="block mt-2 text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {prev.title}
                    </span>
                  </Link>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed border-background-200 text-sm text-foreground-400">
                    已是专题第一篇
                  </div>
                )}
                {next ? (
                  <Link
                    to={`/article/${next.id}`}
                    className="group p-4 rounded-lg border border-background-200 hover:border-primary-300 transition-colors text-right"
                  >
                    <span className="text-xs text-foreground-500 flex items-center gap-1.5 justify-end">
                      下一篇 <i className="ri-arrow-right-line" />
                    </span>
                    <span className="block mt-2 text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {next.title}
                    </span>
                  </Link>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed border-background-200 text-sm text-foreground-400 text-right">
                    已是专题最后一篇
                  </div>
                )}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 flex flex-col gap-6">
                <div className="p-5 rounded-lg border border-background-200 bg-background-100">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">本专题其他整理</h2>
                  <div className="flex flex-col gap-2.5">
                    {siblings
                      .filter((a) => a.id !== article.id)
                      .map((a) => (
                        <Link
                          key={a.id}
                          to={`/article/${a.id}`}
                          className="group flex flex-col gap-1 p-3 rounded-md hover:bg-background-50 transition-colors"
                        >
                          <span className="text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {a.title}
                          </span>
                          <span className="text-xs text-foreground-500">{a.publishTime}</span>
                        </Link>
                      ))}
                  </div>
                  <Link
                    to={`/topic/${topic?.id}`}
                    className="mt-3 flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm hover:bg-secondary-600 transition-colors"
                  >
                    返回专题页
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>

                <div className="p-5 rounded-lg border border-background-200 bg-background-50">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">相关阅读</h2>
                  <div className="flex flex-col gap-3">
                    {moreList.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.id}`}
                        className="group flex flex-col gap-1 pb-3 border-b border-background-200 last:border-0 last:pb-0"
                      >
                        <span className="text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {a.title}
                        </span>
                        <span className="text-xs text-foreground-500">{a.publishTime}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}